import { cn } from "./classNames-DkX30VJU.js";
import { useEffect, useState } from "react";
import { Slot } from "@radix-ui/react-slot";
import { Content, Portal, Provider, Root as Root$1, Trigger } from "@radix-ui/react-tooltip";
import { cva } from "class-variance-authority";
import { jsx, jsxs } from "react/jsx-runtime";
// src/utils/copyToClipboard.ts
/**
* The async Clipboard API requires the document to be focused and rejects with
* `NotAllowedError: Document is not focused` otherwise — which is the normal state
* for a page inside an iframe that has not taken focus, such as Storybook's canvas
* or an embedded preview. `execCommand` has no such requirement because it copies
* the selection of a node we focus ourselves, so it covers that gap.
*/
const copyViaSelection = (text) => {
	const node = document.createElement("textarea");
	node.value = text;
	node.setAttribute("readonly", "");
	node.style.cssText = "position:fixed;top:0;left:0;opacity:0";
	document.body.append(node);
	node.select();
	try {
		return document.execCommand("copy");
	} finally {
		node.remove();
	}
};
const copyToClipboard = async (text) => {
	try {
		await navigator.clipboard.writeText(text);
	} catch (error) {
		if (!copyViaSelection(text)) throw error;
	}
};
// src/utils/createChangeInputEvent.ts
const createChangeInputEvent = (input) => {
	const changeEvent = {
		target: input,
		currentTarget: input,
		type: "change",
		nativeEvent: new Event("change", { bubbles: true }),
		bubbles: true,
		cancelable: false,
		defaultPrevented: false,
		eventPhase: 2,
		isTrusted: false,
		preventDefault: () => {},
		isDefaultPrevented: () => false,
		stopPropagation: () => {},
		isPropagationStopped: () => false,
		persist: () => {},
		timeStamp: Date.now()
	};
	Object.defineProperty(changeEvent, "target", {
		value: input,
		writable: false
	});
	return changeEvent;
};
// src/constants/keyCodes.ts
const keyCodes = {
	ENTER: "Enter",
	ESCAPE: "Escape",
	SPACE: " "
};
// src/components/Tooltip/TooltipContent.tsx
const tooltipVariants = cva([
	"tailwindcss-preflight",
	"tw:inline-block",
	"tw:wrap-anywhere",
	"tw:max-h-96",
	"tw:overflow-y-auto",
	"tw:z-[1060]",
	"tw:[&_hr]:my-3",
	"tw:[&_hr]:border-current/20",
	"tw:[&_p:last-of-type]:mb-0",
	"tw:rounded-popover",
	"tw:border",
	"tw:border-solid",
	"tw:border-border",
	"tw:bg-popover",
	"tw:text-popover-foreground",
	"tw:shadow-md",
	"tw:data-[state=delayed-open]:animate-in",
	"tw:data-[state=instant-open]:animate-in",
	"tw:data-[state=delayed-open]:fade-in-0",
	"tw:data-[state=instant-open]:fade-in-0",
	"tw:data-[state=delayed-open]:zoom-in-95",
	"tw:data-[state=instant-open]:zoom-in-95",
	"tw:data-[state=closed]:animate-out",
	"tw:data-[state=closed]:fade-out-0",
	"tw:data-[state=closed]:zoom-out-95",
	"tw:data-[side=bottom]:slide-in-from-top-2",
	"tw:data-[side=left]:slide-in-from-right-2",
	"tw:data-[side=right]:slide-in-from-left-2",
	"tw:data-[side=top]:slide-in-from-bottom-2"
], {
	defaultVariants: { variant: "action" },
	variants: { variant: {
		action: [
			"tw:max-w-fit",
			"tw:px-3",
			"tw:py-2",
			"tw:text-center",
			"tw:text-xs",
			"tw:font-medium"
		],
		explanatory: [
			"tw:max-w-80",
			"tw:px-4",
			"tw:py-3",
			"tw:text-left",
			"tw:text-sm",
			"tw:font-normal"
		]
	} }
});
const TooltipContent = ({ className, variant, keepOpenOnPointerDownOutside = false, children, onPointerDownOutside, sideOffset = 8, collisionPadding = 8, hideWhenDetached = true, placement = "right", ...rest }) => {
	const handlePointerDownOutside = (event) => {
		if (keepOpenOnPointerDownOutside) event.preventDefault();
		onPointerDownOutside?.(event);
	};
	return /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(Content, {
		...rest,
		sideOffset,
		collisionPadding,
		onPointerDownOutside: handlePointerDownOutside,
		className: tooltipVariants({
			className,
			variant
		}),
		hideWhenDetached,
		side: placement,
		children
	}) });
};
// src/components/Tooltip/TooltipRoot.tsx
const DELAY_SHOW_TOOLTIP = 300;
const TooltipRoot = ({ children, delayDuration = DELAY_SHOW_TOOLTIP, ...rest }) => /* @__PURE__ */ jsx(Root$1, {
	...rest,
	delayDuration,
	children
});
// src/components/Tooltip/TooltipTitle.tsx
/**
* Small uppercase heading above a rich tooltip's body copy, in the tooltip's text colour.
*/
const TooltipTitle = ({ className, ...rest }) => /* @__PURE__ */ jsx("div", {
	className: cn("tw:mb-2 tw:text-xs tw:leading-5 tw:font-semibold tw:uppercase", className),
	...rest
});
// src/components/Tooltip/TooltipTrigger.tsx
/**
* Radix stamps the tooltip's own open/closed `data-state` onto the trigger. Merged onto an
* `asChild` child that keeps a `data-state` of its own — `ToggleGroup.Item`, `Switch`, a `Tabs`
* trigger — it wins, because those primitives spread incoming props *after* their own attribute.
* The child then silently loses every `data-[state=…]` style it has, which is usually the only
* thing marking it as selected.
*
* The tooltip's trigger state is styled nowhere, so it is dropped before it reaches the child.
*/
const WithoutTooltipState = ({ children, ...props }) => {
	const { "data-state": _tooltipState, ...rest } = props;
	return /* @__PURE__ */ jsx(Slot, {
		...rest,
		children
	});
};
const TooltipTrigger = ({ ref, children, className, keepOpenOnClick = false, onClick, onPointerDown, asChild, ...rest }) => {
	const handleClick = (event) => {
		if (keepOpenOnClick) event.preventDefault();
		onClick?.(event);
	};
	const handlePointerDown = (event) => {
		if (keepOpenOnClick) event.preventDefault();
		onPointerDown?.(event);
	};
	return /* @__PURE__ */ jsx(Trigger, {
		...rest,
		asChild: true,
		ref,
		className: cn("tailwindcss-preflight", className),
		onClick: handleClick,
		onPointerDown: handlePointerDown,
		children: asChild ? /* @__PURE__ */ jsx(WithoutTooltipState, { children }) : /* @__PURE__ */ jsx("span", {
			className: "tw:inline-flex tw:items-center",
			children
		})
	});
};
// src/components/Tooltip/Tooltip.tsx
const ACTION_TOOLTIP_LIMIT = 30;
const HINT_UNDERLINE_CLASS_NAME = "tw:cursor-help tw:underline tw:decoration-dashed tw:decoration-muted-foreground/50 tw:underline-offset-2";
/** Mount once at the app root so tooltips share delay state. */
const TooltipProvider = Provider;
/**
* Short plain-text label on hover or focus.
*
* - Use for icon-only buttons, disabled controls, truncated values.
* - Rich content → `HoverCard`; links or buttons → `Popover` (they break a11y here).
* - `variant` defaults to `"explanatory"` for strings over 30 chars, else `"action"`.
* - A disabled `Button` takes `disabledReason` instead.
*/
const Tooltip = ({ tooltip, children, className, keepOpenOnTriggerClick = false, variant, placement = "right", forceHide = false, forceShow = false, delayDuration, triggerAsChild = false, hintUnderline = false }) => {
	const [open, setOpen] = useState(false);
	const resolvedVariant = (() => {
		if (!!variant) return variant;
		if (typeof tooltip === "string" && tooltip.length > ACTION_TOOLTIP_LIMIT) return "explanatory";
		return "action";
	})();
	const isActionVariant = resolvedVariant === "action";
	const handleKeyUp = (event) => {
		if (![keyCodes.ENTER, keyCodes.SPACE].includes(event.key)) return;
		event.stopPropagation();
	};
	return /* @__PURE__ */ jsxs(TooltipRoot, {
		open: !!tooltip && (open || forceShow) && !forceHide,
		onOpenChange: setOpen,
		delayDuration,
		disableHoverableContent: isActionVariant,
		children: [/* @__PURE__ */ jsx(TooltipTrigger, {
			asChild: triggerAsChild,
			keepOpenOnClick: keepOpenOnTriggerClick,
			onKeyUp: handleKeyUp,
			className: hintUnderline ? HINT_UNDERLINE_CLASS_NAME : void 0,
			children
		}), /* @__PURE__ */ jsx(TooltipContent, {
			className,
			variant: resolvedVariant,
			placement,
			keepOpenOnPointerDownOutside: keepOpenOnTriggerClick,
			children: tooltip
		})]
	});
};
Tooltip.Root = TooltipRoot;
Tooltip.Content = TooltipContent;
Tooltip.Trigger = TooltipTrigger;
Tooltip.Title = TooltipTitle;
// src/utils/decorators.tsx
const withTooltipProvider = (Story) => /* @__PURE__ */ jsx(TooltipProvider, { children: /* @__PURE__ */ jsx(Story, {}) });
const withVisualSettle = (Story) => /* @__PURE__ */ jsx("div", {
	"data-visual-settle": true,
	children: /* @__PURE__ */ jsx(Story, {})
});
const withHiddenFullscreen = (Story) => /* @__PURE__ */ jsx("div", {
	className: "tw:[&_[aria-label=Fullscreen]]:hidden",
	children: /* @__PURE__ */ jsx(Story, {})
});
// src/utils/createPrefixedString.ts
const createPrefixedString = (prefix) => (literals) => literals.reduce((acc, literal) => acc + literal, `${prefix}-`);
// src/utils/noop.ts
const noop = () => void 0;
// src/utils/debounce.ts
const debounce = (fn, delay) => {
	let timeoutId;
	const debounced = (...args) => {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => fn(...args), delay);
	};
	debounced.cancel = () => clearTimeout(timeoutId);
	return debounced;
};
// src/utils/isMac.ts
const getIsMac = () => {
	if (typeof navigator !== "undefined" && "userAgentData" in navigator) {
		const platform = navigator.userAgentData.platform;
		if (typeof platform === "string" && platform !== "") return platform.toLowerCase() === "macos";
	}
	if (typeof navigator !== "undefined" && typeof navigator.platform === "string") return navigator.platform.toUpperCase().indexOf("MAC") >= 0;
	return false;
};
const isMac = getIsMac();
// src/utils/useSHA256.ts
const sha256 = async (text) => {
	const msgUint8 = new TextEncoder().encode(text);
	const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
	return Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, "0")).join("");
};
const useSHA256 = (text) => {
	const [hash, setHash] = useState("");
	useEffect(() => {
		if (!text) return;
		let cancelled = false;
		sha256(text).then((h) => {
			if (!cancelled) setHash(h);
		});
		return () => {
			cancelled = true;
		};
	}, [text]);
	return text ? hash : "";
};
// src/utils/splitEntry.ts
/** The separator a typed entry is split on when the caller names none. */
const DEFAULT_ENTRY_SEPARATOR = ",";
/**
* The strings one typed entry stands for.
*
* - Parts are trimmed; blanks dropped.
* - Repeats collapse case-insensitively, matching the combobox's display-string matching.
* - A `null` separator returns the entry verbatim, untrimmed — the caller must reject blank text.
*/
const splitEntry = (entry, separator) => {
	if (separator === null) return [entry];
	const seen = /* @__PURE__ */ new Set();
	return entry.split(separator).map((part) => part.trim()).filter((part) => {
		if (part === "") return false;
		const key = part.toLowerCase();
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};
export { DEFAULT_ENTRY_SEPARATOR, HINT_UNDERLINE_CLASS_NAME, Tooltip, TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger, copyToClipboard, createChangeInputEvent, createPrefixedString, debounce, isMac, keyCodes, noop, splitEntry, useSHA256, withHiddenFullscreen, withTooltipProvider, withVisualSettle };

//# sourceMappingURL=utils-BxEJOnFD.js.map